import { ActorRefFromLogic, assign, emit, setup } from "xstate";
import { adapt, notImplemented, PromiseFn } from ".";
import { ApiError } from "@greeter/api";
import { assert } from "@greeter/assert";

export function invoke<T extends PromiseFn>(
  actor: FetchActor<T>,
  ...args: Parameters<T>
): Promise<Awaited<ReturnType<T>>> {
  return new Promise((resolve, reject) => {
    const sub = actor.on("*", (event) => {
      if (event.type === "fetched") {
        sub.unsubscribe();
        resolve(event.data);
      } else if (event.type === "error") {
        sub.unsubscribe();
        reject(event.error);
      }
    });

    actor.send({ type: "fetch", data: args });
  });
}

/**
 * NOTE: This is very much experimental. Xstate turns out to be rather complicated to
 * compose with generalized functionality.
 *
 * For now it is used only for simple data.
 *
 * We do NOT know how to extend the context with for example indexes and other niceties
 *
 * TODO:
 * - [ ] Figure out how the hell we can extend the context for custom logic which, for example, creates a couple of indexes or caches over the data for easy access. We want to maintain types.
 */
export function createFetchMachine<T extends PromiseFn>(fn: T) {
  return setup({
    types: {} as {
      events: { type: "fetch"; data: Parameters<T> };
      context: {
        input?: Parameters<T>;
        data?: Awaited<ReturnType<T>>;
        error?: ApiError;
      };
      emitted:
        | { type: "fetched"; data: Awaited<ReturnType<T>> }
        | { type: "fetching" }
        | { type: "error"; error: ApiError };
    },
    actors: {
      fetch: notImplemented<T>(),
    },
  }).createMachine({
    initial: "idle",
    states: {
      idle: {
        on: {
          fetch: {
            target: "fetching",
            actions: assign(({ event }) => ({ input: event.data })),
          },
        },
      },
      failed: {
        on: {
          fetch: {
            target: "fetching",
            actions: assign(({ event }) => ({ input: event.data })),
          },
        },
      },
      fetching: {
        entry: emit({ type: "fetching" }),
        invoke: {
          // TODO: This is the only thing that doesn't cause type errors.
          //       Maybe an issue should be opened
          src: adapt(fn),
          input({ context }) {
            assert(context.input, ".input is required in order to fetch");
            return context.input;
          },
          onDone: {
            actions: [
              assign(({ event }) => ({
                data: event.output as Awaited<ReturnType<T>>,
              })),
              emit(({ event }) => ({ type: "fetched", data: event.output })),
            ],
            target: "idle",
          },
          onError: {
            actions: [
              assign(({ event }) => ({
                error: event.error as ApiError,
              })),
              emit(({ event }) => ({
                type: "error",
                error: event.error as ApiError,
              })),
            ],
            target: "failed",
          },
        },
      },
    },
  });
}

export type FetchActor<T extends PromiseFn> = ActorRefFromLogic<
  ReturnType<typeof createFetchMachine<T>>
>;

export function unwrapData<T extends { data?: any }, R extends T["data"]>(s: {
  event: T;
}): R {
  return s.event.data;
}

/**
 * Utility Type: SetterEvents
 *
 * 1. Mapped Type `[K in keyof T]`: Iterates over every key in the object.
 * 2. Template Literal `set.${K & string}`: Construct the "set.keyName" string.
 * 3. `{ type: ..., data: T[K] }`: Creates the object shape for that specific key.
 * 4. `[keyof T]`: Lookup type that converts the mapped object values into a Union.
 */
export type SetterEvents<T, TPrefix = "set"> = {
  [K in keyof T]: {
    type: `${TPrefix & string}.${K & string}`;
    data: T[K];
  };
}[keyof T];

/**
 * Utility Type: FetchActorFields
 *
 * Useful for when having a Requirements interface that you need mapped
 * quickly into a object on a context. See EditTicketEventMachine for example
 */
export type FetchActorFields<T extends Record<string, PromiseFn>> = {
  [K in keyof T as `${string & K}Ref`]: FetchActor<T[K]>;
};
